import { useState, useRef, useEffect, useMemo } from 'react';
import { APP, getFileBadgeColor } from './tokens';
import { AppFolderIcon } from './AppFolderIcon';

// ─── Types ────────────────────────────────────────

export interface SidebarUser {
  name: string;
  email: string;
  plan: 'free' | 'pro' | 'enterprise';
  avatarUrl?: string | null;
  signatureId?: string | null;
  initialsId?: string | null;
  phone?: string;
  address?: string;
  company?: string;
  jobTitle?: string;
  updatedAt?: string | null;
  usage?: { aiRequestsThisMonth: number; pagesThisMonth: number };
}

export interface HistoryItem {
  id: string;
  kind?: 'project' | 'chat';
  projectId?: string;
  conversationId?: string;
  fileName: string;
  fileType: string;
  operation: string;
  timestamp: string;
  isActive: boolean;
  versionCount?: number;
  contextLabel?: string;
  historyLocked?: boolean;
  statusBadge?: 'signed';
  /** Source file ID for risk summary lookup */
  sourceFileId?: string | null;
  /** Risk nudge level — when set, shows a compact risk chip */
  riskNudge?: 'attention' | 'reconsider' | null;
}

/** Primary sidebar tabs beside Projects — Signatures uses its own route, not NavTab. */
export type NavTab = 'archived' | null;

interface SidebarProps {
  open: boolean;
  onToggle: () => void;
  activeNav: NavTab;
  onNavChange: (nav: NonNullable<NavTab>) => void;
  onNewFlow: () => void;
  onSignatures: () => void;
  signaturesActive?: boolean;
  foldersActive?: boolean;
  settingsActive?: boolean;
  onFolders: () => void;
  historyItems: HistoryItem[];
  onHistoryItemClick: (id: string) => void;
  user: SidebarUser | null;
  onUpgrade: () => void;
  /** @deprecated — dropdown now handled internally; kept for backward compat */
  onProfileMenu?: () => void;
  onSettings: () => void;
  onSignOut: () => void;
  onReportBug?: () => void;
  onConnectSource?: (name: string) => void;
  onDisconnectSource?: (name: string) => void;
  onRemoveSource?: (name: string) => void;
  onSourceDocClick?: (sourceName: string, docName: string) => void;
  onBrowseSource?: (name: string) => void;
  /** Map of provider display name (e.g. 'Gmail') → connected boolean from real API */
  integrationStatuses?: Record<string, boolean>;
}

// ─── SVG Icons ────────────────────────────────────

function CornerLogoSvg() {
  return (
    <svg width="22" height="22" viewBox="0 0 256 256" fill="none" style={{ flexShrink: 0 }}>
      <defs>
        <linearGradient id="sidebarLogoGrad" x1="42" y1="38.5" x2="207" y2="209" gradientUnits="userSpaceOnUse">
          <stop stopColor={APP.cornerGradientStart} />
          <stop offset="1" stopColor={APP.cornerGradientEnd} />
        </linearGradient>
      </defs>
      <path d="M236 152V32C236 25.373 230.627 20 224 20H32C25.373 20 20 25.373 20 32L20 224C20 230.627 25.373 236 32 236H152" stroke="url(#sidebarLogoGrad)" strokeWidth="40" strokeLinecap="round" />
    </svg>
  );
}

function CollapseIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0 }}>
      <path d="M10 3L5 8l5 5" stroke={APP.iconDefault} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ flexShrink: 0 }}>
      <path d="M7 2v10M2 7h10" stroke={APP.textGreen} strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

/** Signature hub nav icon — fountain pen / sign motif */
function SignaturesNavIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ flexShrink: 0 }}>
      <path d="M2 11h10M8 3l3 3-5 5H5v-3l5-5z" stroke="currentColor" strokeWidth="1.35" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function NavFolderIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ flexShrink: 0 }}>
      <path d="M2.5 4C2.5 3.45 2.95 3 3.5 3H5.75L7 4.25H10.5C11.05 4.25 11.5 4.7 11.5 5.25V10C11.5 10.55 11.05 11 10.5 11H3.5C2.95 11 2.5 10.55 2.5 10V4Z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
    </svg>
  );
}

function ArchiveIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
      <polyline points="21 8 21 21 3 21 3 8" />
      <rect x="1" y="3" width="22" height="5" />
      <line x1="10" y1="12" x2="14" y2="12" />
    </svg>
  );
}

function SearchIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style={{ flexShrink: 0, opacity: 0.5 }}>
      <circle cx="5.25" cy="5.25" r="3.75" stroke={APP.textPrimary} strokeWidth="1.2" />
      <path d="M8 8L10.5 10.5" stroke={APP.textPrimary} strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  );
}


function DotsIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ flexShrink: 0, opacity: 0.5 }}>
      <circle cx="7" cy="2.5" r="1.2" fill={APP.iconDots} />
      <circle cx="7" cy="7" r="1.2" fill={APP.iconDots} />
      <circle cx="7" cy="11.5" r="1.2" fill={APP.iconDots} />
    </svg>
  );
}

// ─── Sub-components ───────────────────────────────

function NavItem({
  icon,
  label,
  active,
  color,
  fontWeight,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  active?: boolean;
  color?: string;
  fontWeight?: number;
  onClick?: () => void;
}) {
  return (
    <button
      type="button"
      className="btn-hover"
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        borderRadius: APP.sidebarNavItemRadius,
        padding: APP.sidebarNavItemPadding,
        gap: APP.sidebarNavItemGap,
        backgroundColor: active ? APP.bgNavActive : 'transparent',
        border: 'none',
        cursor: 'pointer',
        width: '100%',
        fontFamily: APP.fontFamily,
        color: color ?? (active ? APP.textPrimary : APP.textSecondary),
        fontSize: '13px',
        fontWeight: fontWeight ?? (active ? 500 : 400),
        lineHeight: '16px',
        textAlign: 'left',
        boxSizing: 'border-box',
      }}
    >
      {icon}
      <span style={{ flexShrink: 0 }}>{label}</span>
    </button>
  );
}

// ─── Source row ────────────────────────────────────

// ─── Brand icons for sources ─────────────────────

const integrationLogoStyle: React.CSSProperties = { width: 14, height: 14, flexShrink: 0, objectFit: 'contain' };

function GmailIcon() {
  return <img src="/IntPointsLogos/Gmail_icon_(2020).svg.png" alt="" style={integrationLogoStyle} />;
}

function DropboxIcon() {
  return <img src="/IntPointsLogos/dropbox-2-logo-png-transparent.png" alt="" style={integrationLogoStyle} />;
}

function DriveIcon() {
  return <img src="/IntPointsLogos/Google_Drive_icon_(2020).svg.png" alt="" style={integrationLogoStyle} />;
}

function SlackIcon() {
  return <img src="/IntPointsLogos/Slack_icon_2019.svg.png" alt="" style={integrationLogoStyle} />;
}

function NotionIcon() {
  return <img src="/IntPointsLogos/notion-icon.webp" alt="" style={integrationLogoStyle} />;
}

function LinearIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 100 100" fill="none" style={{ flexShrink: 0 }}>
      <path fill="#222326" d="M1.225 61.523c-.2225-.9485.907-1.546 1.596-.857L39.334 97.178c.6889.689.0915 1.819-.857 1.596C20.052 94.452 5.548 79.948 1.225 61.523ZM.00189135 46.889c-.1764375.283.8887215.56.28957165.761L52.35 99.709c.2007.201.4773.307.7606.29 2.369-.1476 4.694-.46 6.962-.9259.764-.157 1.03-1.096.4782-1.648L2.576 39.449c-.55186-.5519-1.491-.2863-1.648.4782-.465915 2.269-.77832 4.593-.92588465 6.962ZM4.211 29.705c-.16649.374-.8169.811.20765 1.1l64.776 64.776c.2894.289.7262.374 1.1.208 1.786-.7956 3.517-1.693 5.186-2.684.552-.328.637-1.087.1832-1.541L8.436 24.337c-.45409-.4541-1.213-.3689-1.541.1832-.99132 1.668-1.888 3.399-2.684 5.186ZM12.659 18.074c-.3701-.3701-.393-.9637-.0443-1.354C21.779 6.459 35.111 0 49.952 0 77.593 0 100 22.407 100 50.048c0 14.841-6.459 28.172-16.72 37.337-.3903.349-.984.326-1.354-.0443L12.659 18.074Z" />
    </svg>
  );
}

interface SourceDoc {
  name: string;
  status: string; // e.g. 'received', 'sent', 'draft'
}

interface SourceDef {
  name: string;
  icon: React.ReactNode;
  connected: boolean;
  description?: string; // shown when not connected, e.g. "Authorize Notion access"
  docs?: SourceDoc[]; // recent docs shown when connected and expanded
}

/** Static source definitions — connection status comes from integrationStatuses prop */
const SOURCE_DEFS: { name: string; icon: React.ReactNode; description: string }[] = [
  { name: 'Gmail', icon: <GmailIcon />, description: 'Authorize Gmail access' },
  { name: 'Dropbox', icon: <DropboxIcon />, description: 'Authorize Dropbox access' },
  { name: 'Drive', icon: <DriveIcon />, description: 'Authorize Google Drive access' },
  { name: 'Slack', icon: <SlackIcon />, description: 'Authorize Slack access' },
  { name: 'Notion', icon: <NotionIcon />, description: 'Authorize Notion access' },
  { name: 'Linear', icon: <LinearIcon />, description: 'Authorize Linear access' },
];

function SourceRow({
  source, expanded, onToggle, onConnect, onDisconnect, onRemove, onDocClick, onBrowse,
}: {
  source: SourceDef;
  expanded: boolean;
  onToggle: () => void;
  onConnect?: (name: string) => void;
  onDisconnect?: (name: string) => void;
  onRemove?: (name: string) => void;
  onDocClick?: (sourceName: string, docName: string) => void;
  onBrowse?: (name: string) => void;
}) {
  const contentRef = useRef<HTMLDivElement>(null);
  const [measuredHeight, setMeasuredHeight] = useState(0);

  useEffect(() => {
    if (contentRef.current) {
      setMeasuredHeight(contentRef.current.scrollHeight);
    }
  }, [expanded]);

  const EASE = 'cubic-bezier(0.4, 0, 0.2, 1)';

  return (
    <div style={{ flexShrink: 0 }}>
      {/* Row */}
      <div
        onClick={onToggle}
        style={{
          display: 'flex',
          alignItems: 'center',
          height: 28,
          flexShrink: 0,
          paddingBlock: 6,
          gap: 8,
          cursor: 'pointer',
          borderRadius: 4,
          backgroundColor: expanded ? '#00000008' : 'transparent',
          transition: `background-color 200ms ease`,
        }}
      >
        {source.icon}
        <span style={{
          flexGrow: 1,
          fontSize: 13,
          fontFamily: APP.fontFamily,
          fontWeight: expanded && source.connected ? 500 : 400,
          color: source.connected ? APP.textPrimary : '#9C9690',
          lineHeight: '16px',
        }}>
          {source.name}
        </span>
        <div style={{
          width: 4,
          height: 4,
          flexShrink: 0,
          borderRadius: '50%',
          backgroundColor: source.connected ? APP.greenDot : '#C8C3BC',
        }} />
        <svg
          width="10" height="10" viewBox="0 0 10 10" fill="none"
          style={{
            flexShrink: 0,
            opacity: 0.35,
            transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
            transition: `transform 250ms ${EASE}`,
          }}
        >
          <path d="M3.5 2L6.5 5L3.5 8" stroke="#1A1714" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>

      {/* Expansion panel */}
      <div style={{
        overflow: 'hidden',
        height: expanded ? measuredHeight : 0,
        transition: `height 250ms ${EASE}`,
      }}>
        <div ref={contentRef} style={{
          display: 'flex',
          flexDirection: 'column',
          marginLeft: 8,
          paddingTop: 4,
          paddingBottom: 2,
          paddingLeft: 12,
          borderLeft: '1px solid #D4CFC8',
        }}>
          {source.connected ? (
            <>
              {/* Search docs mini-bar */}
              <div
                onClick={(e) => { e.stopPropagation(); onBrowse?.(source.name); }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  height: 22,
                  flexShrink: 0,
                  marginBottom: 4,
                  borderRadius: 4,
                  paddingInline: 6,
                  gap: 5,
                  backgroundColor: '#00000008',
                  cursor: 'pointer',
                }}
              >
                <svg width="10" height="10" viewBox="0 0 12 12" fill="none" style={{ flexShrink: 0, opacity: 0.35 }}>
                  <circle cx="5.25" cy="5.25" r="3.75" stroke="#1A1714" strokeWidth="1.2" />
                  <path d="M8 8L10.5 10.5" stroke="#1A1714" strokeWidth="1.2" strokeLinecap="round" />
                </svg>
                <span style={{ fontSize: 10, color: '#9E9189', fontFamily: APP.fontFamily, lineHeight: '13px' }}>
                  Search docs…
                </span>
              </div>

              {/* Doc list */}
              {source.docs?.map((doc) => (
                <button
                  key={doc.name}
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onDocClick?.(source.name, doc.name); }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    flexShrink: 0,
                    padding: '4px 0',
                    gap: 6,
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    textAlign: 'left',
                  }}
                >
                  <span style={{ flexGrow: 1, fontSize: 11, color: '#1A1714', fontFamily: APP.fontFamily, lineHeight: '14px' }}>
                    {doc.name}
                  </span>
                  <span style={{
                    flexShrink: 0,
                    fontSize: 9,
                    color: '#9E9189',
                    fontFamily: APP.fontFamily,
                    lineHeight: '12px',
                    letterSpacing: '0.02em',
                    borderRadius: 3,
                    padding: '1px 5px',
                    backgroundColor: '#0000000A',
                  }}>
                    {doc.status}
                  </span>
                </button>
              ))}

              {/* Spacer + Disconnect */}
              <div style={{ height: 4, flexShrink: 0 }} />
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onDisconnect?.(source.name); }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  flexShrink: 0,
                  padding: '3px 0',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                }}
              >
                <span style={{ fontSize: 10, color: '#9E9189', fontFamily: APP.fontFamily, lineHeight: '13px' }}>
                  Disconnect
                </span>
              </button>
            </>
          ) : (
            <>
              {/* Connect action */}
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onConnect?.(source.name); }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '4px 0',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                }}
              >
                <svg width="10" height="10" viewBox="0 0 12 12" fill="none" style={{ flexShrink: 0 }}>
                  <path d="M6.5 1.5L11 6L6.5 10.5" stroke="#C4622A" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M1 1.5L5.5 6L1 10.5" stroke="#C4622A" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.35 }} />
                </svg>
                <span style={{ fontSize: 11, fontWeight: 500, color: '#C4622A', fontFamily: APP.fontFamily, lineHeight: '14px' }}>
                  Connect
                </span>
              </button>

              {/* Description */}
              {source.description && (
                <span style={{
                  fontSize: 9,
                  color: '#9E9189',
                  fontFamily: APP.fontFamily,
                  lineHeight: '12px',
                  letterSpacing: '0.01em',
                  paddingLeft: 16,
                  paddingBottom: 6,
                }}>
                  {source.description}
                </span>
              )}

              {/* Remove action */}
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onRemove?.(source.name); }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '4px 0',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                }}
              >
                <svg width="9" height="9" viewBox="0 0 10 10" fill="none" style={{ flexShrink: 0, opacity: 0.5 }}>
                  <path d="M2 2L8 8M8 2L2 8" stroke="#6B6560" strokeWidth="1.2" strokeLinecap="round" />
                </svg>
                <span style={{ fontSize: 10, color: '#9E9189', fontFamily: APP.fontFamily, lineHeight: '13px' }}>
                  Remove
                </span>
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── File type badge ──────────────────────────────

function FileTypeBadge({ type }: { type?: string | null }) {
  const t = (type || 'file').toUpperCase();
  const bg = getFileBadgeColor(type);
  return (
    <div
      style={{
        width: 14,
        height: 16,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        marginTop: 1,
        borderRadius: 2,
        backgroundColor: bg,
      }}
    >
      <span style={{ color: '#FFFFFF', fontFamily: APP.fontFamily, fontSize: 6, fontWeight: 700, lineHeight: '8px' }}>
        {t.slice(0, 3)}
      </span>
    </div>
  );
}

function ProjectFolderBadge({ active, title }: { active?: boolean; title?: string }) {
  return (
    <div
      title={title}
      style={{
        width: 30,
        height: 26,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        borderRadius: 7,
        backgroundColor: active
          ? 'color-mix(in srgb, #4285F4 20%, transparent)'
          : 'color-mix(in srgb, #4285F4 10%, transparent)',
        border: active
          ? '1px solid color-mix(in srgb, #4285F4 36%, transparent)'
          : '1px solid color-mix(in srgb, #4285F4 18%, transparent)',
      }}
    >
      <AppFolderIcon width={20} height={16} />
    </div>
  );
}

// ─── History item ─────────────────────────────────

/** Strip file extensions and replace separators with spaces for cleaner display. */
function cleanTitle(name: string): string {
  return name
    .replace(/\.(pdf|docx?|xlsx?|pptx?|png|jpe?g|txt|csv|rtf|odt)$/i, '')
    .replace(/[_-]/g, ' ')
    .trim();
}

function HistoryRow({ item, onClick }: { item: HistoryItem; onClick: () => void }) {
  const timeAgo = formatTimeAgo(item.timestamp);
  const locked = item.historyLocked;
  const isProject = item.kind === 'project' || Boolean(item.projectId);
  const projectWorkspaceRow = isProject || item.fileType !== 'chat';
  const meta = isProject
    ? `${item.operation} · ${item.versionCount ?? 1} version${(item.versionCount ?? 1) === 1 ? '' : 's'} · ${timeAgo}`
    : `${item.operation} · ${timeAgo}`;
  return (
    <button
      type="button"
      className="btn-hover"
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: projectWorkspaceRow ? 'center' : 'flex-start',
        borderRadius: projectWorkspaceRow ? 8 : 5,
        padding: projectWorkspaceRow ? '9px 10px' : '7px 10px',
        gap: projectWorkspaceRow ? 10 : 8,
        backgroundColor: item.isActive ? APP.bgHistoryActive : 'transparent',
        border: projectWorkspaceRow && item.isActive
          ? '1px solid color-mix(in srgb, #4285F4 28%, transparent)'
          : '1px solid transparent',
        cursor: 'pointer',
        width: '100%',
        textAlign: 'left',
        fontFamily: APP.fontFamily,
        boxSizing: 'border-box',
        opacity: locked ? 0.45 : 1,
      }}
    >
      {projectWorkspaceRow ? (
        <ProjectFolderBadge active={item.isActive} title={isProject ? 'Project workspace' : 'Document workspace'} />
      ) : (
        <FileTypeBadge type={item.fileType} />
      )}
      <div style={{ display: 'flex', flexDirection: 'column', flexGrow: 1, minWidth: 0, gap: 2 }}>
        <span
          style={{
            fontSize: 12,
            fontWeight: 500,
            lineHeight: '16px',
            color: APP.textPrimary,
            display: '-webkit-box',
            WebkitBoxOrient: 'vertical' as const,
            WebkitLineClamp: 2,
            overflow: 'hidden',
          }}
        >
          {cleanTitle(item.fileName)}
        </span>
        {isProject && item.contextLabel && (
          <span
            style={{
              fontSize: 10,
              lineHeight: '13px',
              color: APP.textHistoryMuted,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {item.contextLabel}
          </span>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, minWidth: 0 }}>
          {projectWorkspaceRow && !isProject && (
            <span
              title="Document workspace"
              style={{
                flexShrink: 0,
                borderRadius: 4,
                padding: '1px 5px',
                fontSize: 9,
                fontWeight: 700,
                lineHeight: '13px',
                letterSpacing: '0.04em',
                textTransform: 'uppercase' as const,
                backgroundColor: 'color-mix(in srgb, #8A7F73 10%, transparent)',
                color: APP.textHistoryMuted,
                border: `1px solid ${APP.border}`,
              }}
            >
              Doc
            </span>
          )}
          <span
            title={meta}
            style={{
              fontSize: 11,
              lineHeight: '15px',
              color: APP.textHistoryMuted,
              flex: '1 1 0%',
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {locked ? (
              <>
                <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: '-1px', marginRight: 2 }}>
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0110 0v4" />
                </svg>
                Pro · {timeAgo}
              </>
            ) : (
              <>{meta}</>
            )}
          </span>
          {item.riskNudge && (
            <span
              title={item.riskNudge === 'reconsider' ? 'Some terms may deserve reconsideration' : 'Risk snapshot available'}
              style={{
                fontSize: 10,
                fontWeight: 600,
                letterSpacing: '0.04em',
                padding: '2px 5px',
                borderRadius: 3,
                flexShrink: 0,
                backgroundColor: item.riskNudge === 'reconsider' ? '#FFF3EE' : '#FFF8F0',
                color: item.riskNudge === 'reconsider' ? '#C04A2A' : '#D97645',
                border: `1px solid ${item.riskNudge === 'reconsider' ? '#E8C4B0' : '#F0DBC8'}`,
              }}
            >
              Risk
            </span>
          )}
          {item.statusBadge === 'signed' && (
            <span
              title="Signed version saved"
              style={{
                flexShrink: 0,
                borderRadius: 999,
                backgroundColor: 'color-mix(in srgb, #1B7A4B 12%, transparent)',
                color: '#1B7A4B',
                border: '1px solid color-mix(in srgb, #1B7A4B 22%, transparent)',
                padding: '1px 6px',
                fontSize: 9,
                fontWeight: 700,
                lineHeight: '13px',
              }}
            >
              Signed
            </span>
          )}
        </div>
      </div>
    </button>
  );
}

// ─── Time formatting ──────────────────────────────

function formatTimeAgo(timestamp: string): string {
  const now = Date.now();
  const then = new Date(timestamp).getTime();
  const diffMs = now - then;
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

// ─── Profile dropdown menu ────────────────────────

function SettingsGearIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#1A1714" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  );
}

function ReportBugIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#1A1714" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  );
}

function SignOutMenuIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#C0392B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </svg>
  );
}

function ProfileMenu({
  onSettings,
  onReportBug,
  onSignOut,
  onClose,
  dotsButtonRef,
}: {
  onSettings: () => void;
  onReportBug?: () => void;
  onSignOut: () => void;
  onClose: () => void;
  dotsButtonRef: React.RefObject<HTMLButtonElement | null>;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      // Ignore clicks on the dots toggle button — let the toggle handler manage it
      if (dotsButtonRef.current?.contains(target)) return;
      if (ref.current && !ref.current.contains(target)) onClose();
    };
    const keyHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('keydown', keyHandler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('keydown', keyHandler);
    };
  }, [onClose, dotsButtonRef]);

  const itemStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    paddingBlock: 10,
    paddingInline: 14,
    gap: 9,
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontFamily: APP.fontFamily,
    fontSize: 13,
    fontWeight: 400,
    color: APP.textPrimary,
    lineHeight: '16px',
    textAlign: 'left',
    boxSizing: 'border-box',
  };

  const dividerStyle: React.CSSProperties = {
    height: 1,
    marginInline: 14,
    backgroundColor: '#F0EAE3',
    flexShrink: 0,
  };

  return (
    <div
      ref={ref}
      style={{
        position: 'absolute',
        bottom: 60,
        left: 12,
        right: 12,
        minWidth: 180,
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 10,
        overflow: 'hidden',
        backgroundColor: APP.bgContextMenu,
        border: `1px solid ${APP.borderContextMenu}`,
        boxShadow: APP.shadowContextMenu,
        zIndex: 10,
      }}
    >
      <button type="button" className="btn-hover" onClick={() => { onClose(); onSettings(); }} style={itemStyle}>
        <SettingsGearIcon />
        Settings
      </button>
      <div style={dividerStyle} />
      <button
        type="button"
        className="btn-hover"
        onClick={() => { onClose(); onReportBug?.(); }}
        style={itemStyle}
      >
        <ReportBugIcon />
        Report a bug
      </button>
      <div style={dividerStyle} />
      <button
        type="button"
        className="btn-hover"
        onClick={() => { onClose(); onSignOut(); }}
        style={{ ...itemStyle, color: '#C0392B' }}
      >
        <SignOutMenuIcon />
        Sign out
      </button>
    </div>
  );
}

// ─── Main Sidebar ─────────────────────────────────

export default function Sidebar({
  open,
  onToggle,
  activeNav,
  onNavChange,
  onNewFlow,
  onSignatures,
  signaturesActive,
  foldersActive,
  settingsActive,
  onFolders,
  historyItems,
  onHistoryItemClick,
  user,
  onUpgrade,
  onSettings,
  onSignOut,
  onReportBug,
  onConnectSource,
  onDisconnectSource,
  onRemoveSource,
  onSourceDocClick,
  onBrowseSource,
  integrationStatuses,
}: SidebarProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [expandedSource, setExpandedSource] = useState<string | null>(null);
  const dotsRef = useRef<HTMLButtonElement>(null);

  // Compute sources from static defs + live integration statuses
  const sources: SourceDef[] = useMemo(() =>
    SOURCE_DEFS.map((d) => ({
      ...d,
      connected: integrationStatuses?.[d.name] ?? false,
    })),
    [integrationStatuses],
  );

  const initial = user?.name?.charAt(0)?.toUpperCase() ?? '?';

  // ── Collapsed rail ──────────────────────────────
  if (!open) {
    return (
      <div
        style={{
          width: 48,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          flexShrink: 0,
          backgroundColor: APP.bgSidebar,
          borderRight: `1px solid ${APP.border}`,
          fontFamily: APP.fontFamily,
          boxSizing: 'border-box',
          padding: '12px 0 10px',
          gap: 4,
        }}
      >
        {/* Expand button */}
        <button
          type="button"
          onClick={onToggle}
          title="Expand sidebar"
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: 6,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 6,
            marginBottom: 4,
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={APP.textSecondary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="3" y1="12" x2="21" y2="12" />
            <line x1="3" y1="6" x2="21" y2="6" />
            <line x1="3" y1="18" x2="21" y2="18" />
          </svg>
        </button>
        {/* New Document */}
        <button type="button" onClick={onNewFlow} title="New Document" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 6, display: 'flex', borderRadius: 6 }}>
          <PlusIcon />
        </button>
        {/* Nav icons */}
        <button type="button" onClick={onSignatures} title="Signatures" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 6, display: 'flex', borderRadius: 6 }}>
          <SignaturesNavIcon />
        </button>
        <button type="button" onClick={onFolders} title="Projects" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 6, display: 'flex', borderRadius: 6 }}>
          <NavFolderIcon />
        </button>
        <button type="button" onClick={() => onNavChange('archived')} title="Archived" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 6, display: 'flex', borderRadius: 6 }}>
          <ArchiveIcon />
        </button>

        {/* Spacer */}
        <div style={{ flexGrow: 1 }} />

        {/* Avatar */}
        <button
          type="button"
          onClick={() => setMenuOpen((prev) => !prev)}
          title={user?.name ?? 'Menu'}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: 0,
            display: 'flex',
            position: 'relative',
          }}
        >
          {user?.avatarUrl ? (
            <img
              src={user.avatarUrl}
              alt={user.name}
              style={{
                width: 30,
                height: 30,
                borderRadius: '50%',
                objectFit: 'cover',
                boxShadow: APP.avatarShadow,
              }}
            />
          ) : (
            <div
              style={{
                width: 30,
                height: 30,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '50%',
                backgroundImage: APP.avatarGradient,
                boxShadow: APP.avatarShadow,
              }}
            >
              <span style={{ fontSize: 12, fontWeight: 700, color: '#FFFFFF' }}>{initial}</span>
            </div>
          )}
        </button>
        {menuOpen && (
          <ProfileMenu
            onSettings={onSettings}
            onReportBug={onReportBug}
            onSignOut={onSignOut}
            onClose={() => setMenuOpen(false)}
            dotsButtonRef={dotsRef}
          />
        )}
      </div>
    );
  }

  // ── Full sidebar ────────────────────────────────
  return (
    <div
      style={{
        width: APP.sidebarWidth,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0,
        backgroundColor: APP.bgSidebar,
        borderRight: `1px solid ${APP.border}`,
        fontFamily: APP.fontFamily,
        boxSizing: 'border-box',
      }}
    >
      {/* ── Navigation Section ──────────────────── */}
      <div style={{ display: 'flex', flexDirection: 'column', padding: '12px 12px 8px', gap: 2 }}>
        {/* Logo + collapse */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6, padding: '4px 8px' }}>
          <button
            type="button"
            onClick={onNewFlow}
            style={{ display: 'flex', alignItems: 'center', gap: 7, background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontFamily: APP.fontFamily }}
          >
            <CornerLogoSvg />
            <span style={{ fontSize: 16, fontWeight: 800, color: APP.textPrimary, letterSpacing: '-0.02em', lineHeight: '20px' }}>
              Corner
            </span>
          </button>
          <button type="button" onClick={onToggle} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex' }}>
            <CollapseIcon />
          </button>
        </div>

        {/* New Document */}
        <NavItem icon={<PlusIcon />} label="New Document" color={APP.textGreen} fontWeight={500} onClick={onNewFlow} />

        {/* Nav items — only one active at a time */}
        <NavItem icon={<SignaturesNavIcon />} label="Signatures" active={!!signaturesActive && !foldersActive && !settingsActive} onClick={onSignatures} />
        <NavItem icon={<NavFolderIcon />} label="Projects" active={foldersActive && !signaturesActive && !settingsActive} onClick={onFolders} />
        <NavItem icon={<ArchiveIcon />} label="Archived" active={activeNav === 'archived' && !signaturesActive && !foldersActive && !settingsActive} onClick={() => onNavChange('archived')} />
      </div>

      {/* ── Sources + History (scrollable middle) ── */}
      <div style={{ flexGrow: 1, flexShrink: 1, flexBasis: '0%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Sources */}
        <div style={{ display: 'flex', flexDirection: 'column', paddingRight: 12, paddingBottom: 8, paddingLeft: 12, borderBottom: `1px solid ${APP.border}` }}>
          <span
            style={{
              paddingTop: 12,
              paddingBottom: 6,
              fontSize: 10,
              fontWeight: 600,
              letterSpacing: '0.08em',
              textTransform: 'uppercase' as const,
              lineHeight: '12px',
              color: APP.textMuted,
            }}
          >
            Sources & Integrations
          </span>
          {/* Search bar */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              height: 28,
              flexShrink: 0,
              borderRadius: 6,
              paddingInline: 8,
              gap: 6,
              backgroundColor: APP.bgSearchBar,
            }}
          >
            <SearchIcon />
            <span style={{ fontSize: 11, fontFamily: APP.fontFamily, color: APP.textMuted, lineHeight: '14px' }}>
              Search sources...
            </span>
          </div>
          {/* Source rows */}
          {sources.map((s) => (
            <SourceRow
              key={s.name}
              source={s}
              expanded={expandedSource === s.name}
              onToggle={() => setExpandedSource(expandedSource === s.name ? null : s.name)}
              onConnect={onConnectSource}
              onDisconnect={onDisconnectSource}
              onRemove={onRemoveSource}
              onDocClick={onSourceDocClick}
              onBrowse={onBrowseSource}
            />
          ))}
        </div>

        {/* Projects / History */}
        <div style={{ flexGrow: 1, display: 'flex', flexDirection: 'column', paddingRight: 8, paddingBottom: 12, paddingLeft: 8, overflow: 'auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', padding: '8px 4px' }}>
            <span
              style={{
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: '0.08em',
                textTransform: 'uppercase' as const,
                lineHeight: '14px',
                color: APP.textHistoryMuted,
              }}
            >
              Projects
            </span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {historyItems.map((item) => (
              <HistoryRow key={item.id} item={item} onClick={() => onHistoryItemClick(item.id)} />
            ))}
          </div>
        </div>
      </div>

      {/* ── Profile footer ─────────────────────── */}
      <div
        style={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          paddingBlock: 10,
          paddingInline: 12,
          gap: 10,
          borderTop: `1px solid ${APP.border}`,
        }}
      >
        {menuOpen && (
          <ProfileMenu
            onSettings={onSettings}
            onReportBug={onReportBug}
            onSignOut={onSignOut}
            onClose={() => setMenuOpen(false)}
            dotsButtonRef={dotsRef}
          />
        )}
        {/* Avatar */}
        {user?.avatarUrl ? (
          <img
            src={user.avatarUrl}
            alt={user.name}
            style={{
              width: 34,
              height: 34,
              flexShrink: 0,
              borderRadius: '50%',
              objectFit: 'cover',
              boxShadow: APP.avatarShadow,
            }}
          />
        ) : (
          <div
            style={{
              width: 34,
              height: 34,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              borderRadius: '50%',
              backgroundImage: APP.avatarGradient,
              boxShadow: APP.avatarShadow,
            }}
          >
            <span style={{ fontSize: 13, fontWeight: 700, color: '#FFFFFF', letterSpacing: '-0.01em', lineHeight: '16px' }}>
              {initial}
            </span>
          </div>
        )}

        {/* Name + plan */}
        <div style={{ flexGrow: 1, flexShrink: 1, flexBasis: '0%', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>
          <div style={{ overflow: 'hidden' }}>
            <div
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: APP.textPrimary,
                lineHeight: '13px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {user?.name ?? 'Guest'}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                borderRadius: 999,
                paddingBlock: 2,
                paddingInline: 8,
                backgroundColor: user?.plan === 'enterprise' ? APP.bgEnterpriseBadge : user?.plan === 'pro' ? APP.bgProBadge : APP.bgFreeBadge,
                border: `1px solid ${user?.plan === 'enterprise' ? APP.borderEnterpriseBadge : user?.plan === 'pro' ? APP.borderProBadge : APP.borderFreeBadge}`,
                fontSize: 10,
                fontWeight: 600,
                letterSpacing: '0.02em',
                color: user?.plan === 'enterprise' ? APP.textEnterpriseBadge : user?.plan === 'pro' ? APP.textProBadge : APP.textFreeBadge,
                lineHeight: '12px',
              }}
            >
              {user?.plan === 'enterprise' ? 'Enterprise' : user?.plan === 'pro' ? 'Pro' : 'Free'}
            </span>
            {user?.plan === 'free' && (
              <>
                <span style={{ fontSize: 10, color: APP.textDot, lineHeight: '12px' }}>·</span>
                <button
                  type="button"
                  onClick={onUpgrade}
                  style={{
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    cursor: 'pointer',
                    fontSize: 10,
                    fontWeight: 500,
                    fontFamily: APP.fontFamily,
                    color: APP.textUpgrade,
                    lineHeight: '12px',
                    textDecorationLine: 'underline',
                    textDecorationColor: APP.textUpgradeDecor,
                    textDecorationThickness: '1px',
                    textUnderlineOffset: '2px',
                  }}
                >
                  Upgrade
                </button>
              </>
            )}
          </div>
        </div>

        {/* Menu dots */}
        <button
          ref={dotsRef}
          type="button"
          onClick={() => setMenuOpen((prev) => !prev)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', flexShrink: 0 }}
        >
          <DotsIcon />
        </button>
      </div>
    </div>
  );
}
